import { Button } from '@praetorian-chariot/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';

import { GraphExplorerMultiSelectDropdown } from '@/components/nodeGraph/GraphExplorerMultiSelectDropdown';
import { ResourceType } from '@/types';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { omit } from '@/utils/lodash.util';

import { FiltersSectionProps } from './types';

/**
 * FiltersSection - Advanced filtering controls for graph entities
 *
 * This section provides multi-select dropdown filters for various entity attributes
 * such as status, country, cloud service, etc. Filters are dynamically loaded based
 * on the selected entity type (Asset vs Seed) and applied to the graph query.
 *
 * Features:
 * - Dynamic filter options based on entity type
 * - Multi-select dropdowns with search
 * - Clear individual filters or all filters
 * - Collapsible section with accordion behavior
 * - Loading states for filter options
 * - Visual indicator when filters are applied
 *
 * @param filters - Current filter state
 * @param setFilters - Callback to update filters
 * @param clusterByEntity - Current clustering entity type
 * @param isExpanded - Whether section is expanded
 * @param onToggle - Callback to toggle section expansion
 * @param panelRef - Reference to parent panel for dropdown positioning
 */
export const FiltersSection: FC<FiltersSectionProps> = ({
  filters,
  setFilters,
  clusterByEntity,
  isExpanded,
  onToggle,
  panelRef,
}) => {
  const [filterSearch, setFilterSearch] = useState('');
  const [, setOpenDropdownId] = useState<string | null>(null);

  // Get filter options for the current entity type
  const entityTypeForFilters =
    clusterByEntity === ResourceType.Seed ? 'Seed' : 'Asset';
  const {
    filterOptions,
    availableFilters,
    sortedOptions,
    status: filterOptionsStatus,
  } = useEntityFilters(entityTypeForFilters, 'graph-explorer');

  const isDropdownFiltersApplied = useMemo(() => {
    return (
      filters?.advancedFilters &&
      Object.values(filters.advancedFilters).find(value =>
        Boolean(Array.isArray(value) && value.length)
      )
    );
  }, [filters]);

  const handleDropdownStateChange = useCallback(
    (isOpen: boolean, id: string) => {
      if (isOpen) {
        setFilterSearch('');
        setOpenDropdownId(id);
      } else {
        // Clear the open dropdown ID when closing to prevent state desync
        setOpenDropdownId(null);
      }
    },
    []
  );

  return (
    <div className="flex flex-col gap-2 pb-2">
      <button
        onClick={onToggle}
        className="text-chariot-text-primary hover:text-chariot-text-primary-hover flex items-center gap-1 p-0 text-left text-sm font-semibold transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="size-4 shrink-0" />
        ) : (
          <ChevronRight className="size-4 shrink-0" />
        )}
        Set Filters
      </button>
      {isExpanded && (
        <div className="ml-2 flex flex-col gap-2">
          {!sortedOptions || sortedOptions.length === 0 ? (
            <div className="text-chariot-text-secondary text-sm">
              {filterOptionsStatus === 'pending'
                ? 'Loading filters...'
                : 'No filters available'}
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                {sortedOptions.map((option: any) => {
                  const key = option.value as string;
                  // Use availableFilters for Assets, filterOptions for other entity types
                  const filterData = availableFilters || filterOptions;
                  const filterOption =
                    filterData &&
                    typeof filterData === 'object' &&
                    !Array.isArray(filterData)
                      ? (filterData as any)[key]
                      : null;
                  if (!filterOption) return null;

                  return (
                    <div key={key} className="w-full">
                      <GraphExplorerMultiSelectDropdown
                        label={option.label || key}
                        options={filterOption || []}
                        selectedValues={filters?.advancedFilters?.[key] || []}
                        onApply={(selected: any) => {
                          setFilters((prev: any) => ({
                            ...prev,
                            advancedFilters:
                              selected && selected.length > 0
                                ? {
                                    ...prev.advancedFilters,
                                    [key]: selected.map(
                                      (option: any) =>
                                        option.value?.toString() || ''
                                    ),
                                  }
                                : omit(prev.advancedFilters, key),
                          }));
                        }}
                        onClear={() => {
                          setFilters((prev: any) => ({
                            ...prev,
                            advancedFilters: omit(prev.advancedFilters, key),
                          }));
                        }}
                        searchValue={filterSearch}
                        setSearchValue={setFilterSearch}
                        isLoading={filterOptionsStatus === 'pending'}
                        onDropdownStateChange={handleDropdownStateChange}
                        anchorRef={panelRef}
                        size="xs"
                      />
                    </div>
                  );
                })}
              </div>
              {isDropdownFiltersApplied && (
                <Button
                  styleType="rounded"
                  className="text-chariot-brand-primary self-start border-none"
                  label="Clear Filters"
                  onClick={() =>
                    setFilters((filters: any) => ({
                      ...filters,
                      advancedFilters: {},
                    }))
                  }
                  size="sm"
                />
              )}
            </>
          )}
        </div>
      )}
      {isExpanded && <div className="border-chariot-stroke mt-2 border-t" />}
    </div>
  );
};
